/**
 * Mitad «fuente» de la ventana de documento (DESIGN.md §11.7): el texto reconstruido en párrafos («¶ n» en el margen,
 * ancla `#p-n`, medida ≤ 66ch, 15 px por defecto y A−/A+ entre 14 y 17 recordado) con un buscador interno («1 de N»,
 * ↑ ↓) cuyas coincidencias operan sobre el texto reconstruido, en `indigo-50` (la actual en `indigo-200`). Si el
 * original se puede mostrar (PDF de R2), una segunda vista lo muestra en la página; si no, un enlace a su sitio.
 * Todo con nodos de React: el texto del documento nunca se interpreta como HTML.
 *
 * Una ley (m6) conserva el texto tal cual con su índice por artículo fijo de 96 px (las posiciones del índice son
 * sobre el texto crudo), sin párrafos numerados.
 */
import { ChangeEvent, KeyboardEvent, ReactNode, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import type { ArticuloTexto } from '../../utils/normasFila';
import { ChevronDown, ChevronUp, ExternalLink, Search } from 'lucide-react';
import { resaltarTramos, Tramo } from '../../utils/highlighting';
import { Bloque, Llamada, Nota, numerarParrafos, reflow } from '../../utils/parrafos';
import { miles } from '../../utils/bibliotecas';
import { FOCO } from '../biblioteca/piezas';
import { Segmentado } from '../biblioteca/BarraFiltros';

/** Caracteres del texto íntegro que se muestran antes de «Mostrar el texto completo» (hay sentencias de 1,2 millones). */
export const LIMITE_TEXTO = 30000;
export const TAMANO_MIN = 14;
export const TAMANO_MAX = 17;
export const TAMANO_DEFECTO = 15;
const CLAVE_TAMANO = 'alertai_texto_tamano';

function leerTamano(): number {
  try {
    const n = Number(window.localStorage.getItem(CLAVE_TAMANO));
    if (n >= TAMANO_MIN && n <= TAMANO_MAX) return n;
  } catch { /* sin almacenamiento */ }
  return TAMANO_DEFECTO;
}

/**
 * Dibuja los tramos (texto y coincidencias) e intercala una marca vacía al comienzo de cada artículo del índice, para
 * saltar a él y saber cuál está a la vista; la línea «Artículo N» va en 600 (m6). Un tramo que cruza el comienzo o el
 * fin de esa línea se parte ahí; una coincidencia no se parte (la marca va antes de ella).
 */
function conTramos(tramos: Tramo[], articulos: { inicio: number; largo: number }[], dibujar: (t: Tramo, clave: string) => ReactNode): ReactNode[] {
  const out: ReactNode[] = [];
  const cortes = articulos.flatMap((a, k) => [{ pos: a.inicio, k, tipo: 'inicio' as const }, { pos: a.inicio + a.largo, k, tipo: 'fin' as const }]);
  let pos = 0;
  let c = 0;
  let enRotulo = false;
  const despues: number[] = [];
  const marca = (k: number) => <span key={`art-${k}`} data-articulo={k} className="block scroll-mt-3" aria-hidden="true" />;
  const pieza = (texto: string, resaltado: boolean, clave: string) => (enRotulo && !resaltado
    ? <span key={clave} className="font-semibold text-slate-900">{texto}</span>
    : dibujar({ texto, resaltado }, clave));
  tramos.forEach((t, i) => {
    let resto = t.texto;
    let parte = 0;
    while (c < cortes.length && cortes[c].pos < pos + resto.length) {
      const corte = Math.max(cortes[c].pos - pos, 0);
      if (t.resaltado) {
        // Una coincidencia no se parte: si el artículo empieza en ella, la marca va antes; si empieza dentro (cruza
        // el salto de línea), después; en ambos casos el resto del rótulo sigue en 600.
        if (cortes[c].tipo === 'inicio') {
          if (corte === 0) out.push(marca(cortes[c].k)); else despues.push(cortes[c].k);
          enRotulo = true;
        } else enRotulo = false;
        c += 1;
        continue;
      }
      if (corte > 0) out.push(pieza(resto.slice(0, corte), false, `${i}-${parte++}`));
      if (cortes[c].tipo === 'inicio') { out.push(marca(cortes[c].k)); enRotulo = true; } else enRotulo = false;
      pos += corte;
      resto = resto.slice(corte);
      c += 1;
    }
    if (resto) out.push(pieza(resto, t.resaltado, `${i}-${parte}`));
    despues.splice(0).forEach(k => out.push(marca(k)));
    pos += resto.length;
  });
  for (; c < cortes.length; c += 1) if (cortes[c].tipo === 'inicio') out.push(marca(cortes[c].k));
  return out;
}

/**
 * Dibuja los tramos de un párrafo reconstruido e intercala las llamadas a nota (superíndice) en su posición. Una
 * coincidencia no se parte: una llamada que cae dentro de ella va justo después.
 */
function conLlamadas(tramos: Tramo[], llamadas: Llamada[], dibujar: (t: Tramo, clave: string) => ReactNode): ReactNode[] {
  const out: ReactNode[] = [];
  const sup = (l: Llamada, clave: string) => <sup key={clave} className="text-[0.7em] text-indigo-700 ml-px" data-nota={l.n}>{l.n}</sup>;
  let pos = 0;
  let c = 0;
  tramos.forEach((t, i) => {
    let resto = t.texto;
    let parte = 0;
    while (!t.resaltado && c < llamadas.length && llamadas[c].pos < pos + resto.length) {
      const corte = Math.max(llamadas[c].pos - pos, 0);
      if (corte > 0) out.push(dibujar({ texto: resto.slice(0, corte), resaltado: false }, `${i}-${parte++}`));
      out.push(sup(llamadas[c], `${i}-n${c}`));
      pos += corte;
      resto = resto.slice(corte);
      c += 1;
    }
    if (resto) out.push(dibujar({ texto: resto, resaltado: t.resaltado }, `${i}-${parte}`));
    pos += resto.length;
    while (c < llamadas.length && llamadas[c].pos <= pos) { out.push(sup(llamadas[c], `${i}-n${c}`)); c += 1; }
  });
  for (; c < llamadas.length; c += 1) out.push(sup(llamadas[c], `fin-n${c}`));
  return out;
}

interface Unidad {
  clave: string;
  tipo: 'cabecera' | 'bloque' | 'firma' | 'nota';
  texto: string;
  tramos: Tramo[];
  bloque?: Bloque & { n: number | null };
  nota?: Nota;
}

/** Nombre corto del sitio de origen para la barra del texto: «OJV», «LeyChile», «sii.cl». */
const sitioCorto = (sitio: string) => (sitio === 'la OJV del TTA' ? 'OJV' : sitio === 'el archivo original' ? 'Original' : sitio || 'Original');

export function TextoDocumento({ texto, titulo, nombreTexto, minutos, consultaInicial = '', pdf, origen, sitio, indice, placeholder = 'Buscar en el texto', centrado = false, parrafoInicial = null, onParrafoActivo }: {
  texto: string;
  titulo: string;
  /** «Texto del oficio», «Texto de la sentencia»… */
  nombreTexto: string;
  minutos: number;
  consultaInicial?: string;
  /** PDF que se puede mostrar dentro de la página (R2). */
  pdf: string | null;
  /** Enlace http(s) al original. */
  origen: string | null;
  sitio: string;
  /** Índice por artículo (leyes): posiciones de cada «Artículo N» en `texto`. */
  indice?: ArticuloTexto[];
  placeholder?: string;
  /** Con el panel plegado la columna del texto se centra. */
  centrado?: boolean;
  /** `#p-k` de la URL: se desplaza a ese párrafo y queda activo. */
  parrafoInicial?: number | null;
  /** Párrafo activo (el de `#p-k` o el último «¶ n» pulsado), para «Copiar cita». */
  onParrafoActivo?: (n: number | null) => void;
}) {
  const [vista, setVista] = useState<'texto' | 'original'>(texto ? 'texto' : 'original');
  const [consulta, setConsulta] = useState(consultaInicial);
  // La búsqueda que viene de la lista resalta cada término (como la lista); lo que se escribe aquí, la frase entera.
  const [porFrase, setPorFrase] = useState(!consultaInicial);
  const [actual, setActual] = useState(0);
  const [completo, setCompleto] = useState(false);
  const [tamano, setTamano] = useState(leerTamano);
  const [parrafoActivo, setParrafoActivo] = useState<number | null>(null);
  const caja = useRef<HTMLDivElement>(null);
  const conIndice = !!indice && indice.length >= 2;
  const [activo, setActivo] = useState(0);

  // La búsqueda recorre textos de hasta 1,2 millones de caracteres: se difiere para no trabar el campo.
  const diferida = useDeferredValue(consulta.trim().length >= 2 ? consulta.trim() : '');
  // Con índice por artículo se muestra la ley entera: cada artículo del índice tiene que estar en la página.
  const visible = completo || conIndice || diferida || texto.length <= LIMITE_TEXTO ? texto : texto.slice(0, LIMITE_TEXTO);
  // Texto reconstruido en párrafos (todo salvo las leyes, que conservan el crudo con su índice).
  const reconstruido = useMemo(() => (conIndice || !visible ? null : reflow(visible, titulo)), [visible, titulo, conIndice]);
  const unidades = useMemo<Unidad[] | null>(() => {
    if (!reconstruido) return null;
    const u: Omit<Unidad, 'tramos'>[] = [];
    if (reconstruido.cabecera) u.push({ clave: 'cab', tipo: 'cabecera', texto: reconstruido.cabecera });
    numerarParrafos(reconstruido.bloques).forEach((b, i) => u.push({ clave: `b${i}`, tipo: 'bloque', texto: b.texto, bloque: b }));
    reconstruido.firma.forEach((l, i) => u.push({ clave: `f${i}`, tipo: 'firma', texto: l }));
    reconstruido.notas.forEach((nt, i) => u.push({ clave: `n${i}`, tipo: 'nota', texto: nt.texto, nota: nt }));
    return u.map(x => ({ ...x, tramos: resaltarTramos(x.texto, diferida, 2, porFrase) }));
  }, [reconstruido, diferida, porFrase]);
  const tramos = useMemo(() => (reconstruido ? [] : resaltarTramos(visible, diferida, 2, porFrase)), [reconstruido, visible, diferida, porFrase]);
  const total = useMemo(
    () => (unidades ? unidades.reduce((s, u) => s + u.tramos.filter(t => t.resaltado).length, 0) : tramos.filter(t => t.resaltado).length),
    [unidades, tramos],
  );

  useEffect(() => { setActual(0); }, [diferida]);
  // La coincidencia actual se lleva a la vista dentro de la mitad del texto.
  useEffect(() => {
    if (!total) return;
    caja.current?.querySelector(`[data-coincidencia="${actual}"]`)?.scrollIntoView?.({ block: 'center' });
  }, [actual, total]);

  // Tamaño del texto recordado (14–17).
  useEffect(() => {
    try { window.localStorage.setItem(CLAVE_TAMANO, String(tamano)); } catch { /* sin almacenamiento */ }
  }, [tamano]);

  // «#p-k»: el párrafo queda activo y a la vista.
  useEffect(() => {
    if (!parrafoInicial || !reconstruido) return;
    // Solo si el párrafo existe (un texto cortado a LIMITE_TEXTO puede no llegar a él).
    const el = caja.current?.querySelector(`#p-${parrafoInicial}`);
    if (!el) return;
    setParrafoActivo(parrafoInicial);
    el.scrollIntoView?.({ block: 'start' });
  }, [parrafoInicial, reconstruido]);
  useEffect(() => { onParrafoActivo?.(parrafoActivo); }, [parrafoActivo, onParrafoActivo]);

  // Artículo activo: el último cuya marca ya pasó el borde superior de la mitad del texto.
  useEffect(() => {
    const el = caja.current;
    if (!conIndice || !el) return;
    let cuadro = 0;
    const medir = () => {
      cuadro = 0;
      const marcas = Array.from(el.querySelectorAll<HTMLElement>('[data-articulo]'));
      const tope = el.getBoundingClientRect().top + 24;
      let i = 0;
      marcas.forEach((m, k) => { if (m.getBoundingClientRect().top <= tope) i = k; });
      setActivo(i);
    };
    const alMover = () => { if (!cuadro) cuadro = requestAnimationFrame(medir); };
    el.addEventListener('scroll', alMover, { passive: true });
    return () => { el.removeEventListener('scroll', alMover); if (cuadro) cancelAnimationFrame(cuadro); };
  }, [conIndice, vista]);
  // El artículo activo queda a la vista dentro del índice (el DL 824 tiene 181).
  const indiceNav = useRef<HTMLElement>(null);
  useEffect(() => {
    indiceNav.current?.querySelector('[aria-current="location"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [activo]);
  const irArticulo = (k: number) => {
    setActivo(k);
    caja.current?.querySelector(`[data-articulo="${k}"]`)?.scrollIntoView?.({ block: 'start' });
  };

  const mover = (paso: number) => { if (total) setActual(a => (a + paso + total) % total); };
  const tecla = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); mover(e.shiftKey ? -1 : 1); }
  };

  let n = -1;
  const dibujar = (t: Tramo, i: string) => {
    if (!t.resaltado) return <span key={i}>{t.texto}</span>;
    n += 1;
    return (
      <mark key={i} data-coincidencia={n} className={`rounded-[3px] text-slate-900 ${n === actual ? 'bg-indigo-200' : 'bg-indigo-50'}`}>{t.texto}</mark>
    );
  };
  const botonTamano = (etiqueta: string, texto: string, paso: number, activo: boolean, tope: boolean) => (
    <button
      type="button"
      onClick={() => setTamano(t => Math.min(TAMANO_MAX, Math.max(TAMANO_MIN, t + paso)))}
      disabled={tope}
      aria-label={etiqueta}
      title={etiqueta}
      className={`w-[26px] h-[26px] grid place-items-center rounded-md text-[12px] font-semibold disabled:opacity-40 ${activo ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'} ${FOCO}`}
    >
      {texto}
    </button>
  );

  return (
    <section className="h-full flex flex-col min-h-0 bg-slate-50" aria-label={nombreTexto}>
      <div className="h-[60px] shrink-0 px-8 flex items-center justify-between gap-3 border-b border-slate-200/70">
        <div className="flex items-center gap-3 min-w-0 flex-1 text-[13px]">
          {texto && pdf ? (
            <Segmentado etiqueta="Vista de la fuente" valor={vista} onChange={setVista} opciones={[{ valor: 'texto', texto: nombreTexto }, { valor: 'original', texto: 'Original' }]} />
          ) : (
            <span className="font-medium text-slate-700 whitespace-nowrap shrink-0">{texto ? nombreTexto : 'Documento original'}</span>
          )}
          {vista === 'texto' && conIndice && <span className="text-slate-500 whitespace-nowrap">· índice por artículo</span>}
          {vista === 'texto' && minutos > 0 && !conIndice && <span className="text-slate-500 tabular-nums whitespace-nowrap" title={`${minutos} min de lectura`}>· {minutos} min</span>}
          {vista === 'texto' && reconstruido && (
            <div className="flex items-center gap-0.5 ml-1 shrink-0" role="group" aria-label="Tamaño del texto">
              {botonTamano('Reducir el texto', 'A−', -1, tamano < TAMANO_DEFECTO, tamano <= TAMANO_MIN)}
              {botonTamano('Ampliar el texto', 'A+', 1, tamano > TAMANO_DEFECTO, tamano >= TAMANO_MAX)}
            </div>
          )}
          {/* Con PDF, «Original» es la pestaña y, en ella, «Abrir en una pestaña nueva»; sin PDF, el enlace a su sitio */}
          {vista === 'original' && pdf && (
            <a href={pdf} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 whitespace-nowrap rounded ${FOCO}`}>
              Abrir en una pestaña nueva <ExternalLink className="w-3 h-3 opacity-60" aria-hidden="true" />
            </a>
          )}
          {origen && !pdf && (
            <a href={origen} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 min-w-0 text-slate-500 hover:text-slate-900 whitespace-nowrap rounded ${FOCO}`}>
              <span className="truncate" title={sitio ? `Abrir en ${sitio}` : 'Original'}>{sitioCorto(sitio)}</span> <ExternalLink className="w-3 h-3 opacity-60" aria-hidden="true" />
            </a>
          )}
        </div>
        {vista === 'texto' && texto && (
          <div className="h-9 w-[240px] min-w-[200px] shrink flex items-center gap-2 rounded-[10px] border border-slate-200 bg-white px-2.5 focus-within:border-indigo-200">
            <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" />
            <input
              type="search"
              value={consulta}
              onChange={(e: ChangeEvent<HTMLInputElement>) => { setConsulta(e.target.value); setPorFrase(true); }}
              onKeyDown={tecla}
              placeholder={placeholder}
              aria-label="Buscar en el texto"
              className="flex-1 min-w-0 text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none bg-transparent"
            />
            {diferida && (
              <>
                <span className="text-[12px] text-slate-500 tabular-nums whitespace-nowrap" role="status">
                  {total ? `${miles(actual + 1)} de ${miles(total)}` : 'Sin coincidencias'}
                </span>
                <button type="button" onClick={() => mover(-1)} disabled={!total} aria-label="Coincidencia anterior" className={`p-0.5 rounded text-slate-500 hover:text-slate-900 disabled:opacity-40 ${FOCO}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => mover(1)} disabled={!total} aria-label="Coincidencia siguiente" className={`p-0.5 rounded text-slate-500 hover:text-slate-900 disabled:opacity-40 ${FOCO}`}><ChevronDown className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        )}
      </div>

      <div className={`flex-1 min-h-0 ${conIndice && vista === 'texto' ? 'grid grid-cols-[96px_minmax(0,1fr)]' : 'flex flex-col'}`}>
      {conIndice && vista === 'texto' && (
        <nav ref={indiceNav} aria-label="Índice por artículo" className="min-h-0 overflow-y-auto border-r border-slate-200/70 py-5 px-2 space-y-0.5">
          {indice!.map((a, k) => (
            <button
              key={`${a.inicio}`}
              type="button"
              onClick={() => irArticulo(k)}
              aria-current={k === activo ? 'location' : undefined}
              className={`block w-full text-left text-[13px] px-2 py-1 rounded-md tabular-nums truncate ${FOCO} ${k === activo ? 'bg-white text-indigo-700 font-medium shadow-[0_0_0_1px_rgba(15,23,42,.06)]' : 'text-slate-500 hover:text-slate-900'}`}
            >
              {a.etiqueta}
            </button>
          ))}
        </nav>
      )}
      <div ref={caja} className="flex-1 min-h-0 overflow-y-auto px-8 py-6">
        {vista === 'texto' && texto ? (
          <>
            {unidades ? (
              <div className={`max-w-[66ch] text-slate-800 break-words ${centrado ? 'mx-auto' : ''}`} style={{ fontSize: tamano }}>
                {unidades.map(u => {
                  if (u.tipo === 'cabecera') {
                    return <div key={u.clave} className="text-[12px] leading-[1.5] tracking-[.02em] text-slate-500 mb-3.5">{u.tramos.map((t, i) => dibujar(t, `${u.clave}-${i}`))}</div>;
                  }
                  if (u.tipo === 'bloque' && u.bloque) {
                    const b = u.bloque;
                    if (b.tipo === 'h4') {
                      return <h4 key={u.clave} className="text-[13px] font-semibold tracking-[.02em] text-slate-900 mt-[18px] mb-2">{u.tramos.map((t, i) => dibujar(t, `${u.clave}-${i}`))}</h4>;
                    }
                    return (
                      <p key={u.clave} id={`p-${b.n}`} className={`scroll-mt-4 leading-[1.75] ${b.tipo === 'li' ? 'pl-[1.6em] -indent-[1.6em] mb-1.5' : 'mb-3'}`}>
                        {conLlamadas(u.tramos, b.llamadas, (t, i) => dibujar(t, `${u.clave}-${i}`))}
                      </p>
                    );
                  }
                  if (u.tipo === 'firma') {
                    const primera = u.clave === 'f0';
                    const segunda = u.clave === 'f1';
                    return (
                      <div key={u.clave} className={`text-[13.5px] leading-[1.6] text-slate-600 ${primera ? 'mt-[18px]' : ''} ${segunda ? 'font-semibold text-slate-800' : ''}`}>
                        {u.tramos.map((t, i) => dibujar(t, `${u.clave}-${i}`))}
                      </div>
                    );
                  }
                  return (
                    <p key={u.clave} className={`text-[12.5px] leading-[1.6] text-slate-500 mb-1.5 ${u.clave === 'n0' ? 'mt-[22px] pt-3 border-t border-slate-200' : ''}`}>
                      <sup className="mr-1 text-indigo-700">{u.nota?.n}</sup>
                      {u.tramos.map((t, i) => dibujar(t, `${u.clave}-${i}`))}
                    </p>
                  );
                })}
              </div>
            ) : (
              <div className="max-w-[72ch] text-[15px] leading-[1.7] text-slate-800 whitespace-pre-line break-words">
                {conTramos(tramos, conIndice ? indice! : [], dibujar)}
              </div>
            )}
            {visible.length < texto.length && (
              <button type="button" onClick={() => setCompleto(true)} className={`mt-4 text-[13px] font-medium text-indigo-700 hover:underline rounded ${FOCO}`}>
                Mostrar el texto completo <span className="tabular-nums">({miles(texto.length)} caracteres)</span>
              </button>
            )}
          </>
        ) : pdf ? (
          <iframe src={pdf} title={`Documento original: ${titulo}`} className="w-full h-full min-h-[420px] rounded-[10px] border border-slate-200 bg-white" />
        ) : (
          <p className="text-[13px] text-slate-500">
            {origen ? <>El original no se puede mostrar dentro de AlertAI. <a href={origen} target="_blank" rel="noopener noreferrer" className={`font-medium text-indigo-700 hover:underline rounded ${FOCO}`}>Abrir en {sitio || 'su sitio'}</a>.</> : 'Este documento no tiene texto ni enlace al original.'}
          </p>
        )}
      </div>
      </div>
    </section>
  );
}
